import { ShopifyAppApiError } from '@/lib/api';

export interface ErrorCardCopy {
  title: string;
  body: string;
}

function codeOf(err: unknown): string {
  return err instanceof ShopifyAppApiError ? err.code : 'UNKNOWN';
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Full-card copy for the top-level loading/connection error state.
 */
export function errorCardCopy(err: unknown): ErrorCardCopy {
  switch (codeOf(err)) {
    case 'APP_BRIDGE_TIMEOUT':
      return {
        title: "Shopify isn't ready yet",
        body: 'App Bridge took too long to load. Reload from your Shopify admin, or check whether a browser extension is blocking the embedded script.',
      };
    case 'TIMEOUT':
      return {
        title: 'Buds took too long to respond',
        body: 'The request timed out. Buds may be slow or temporarily unreachable.',
      };
    case 'BUDS_UNREACHABLE':
      return {
        title: "Can't reach Buds",
        body: 'We could not connect to the Buds servers. Check your connection and try again.',
      };
    case 'INVALID_KEY':
      return {
        title: 'Authentication failed',
        body: 'Your Shopify session was rejected by Buds. Reload the page from your Shopify admin.',
      };
    case 'CONFIGURATION':
      return {
        title: 'Buds is misconfigured for this shop',
        body: 'Contact Buds support so we can fix this on our side.',
      };
    default:
      return {
        title: "Couldn't load this page",
        body: messageOf(err),
      };
  }
}

/**
 * Single-line inline message for form-level errors (e.g. submit failed).
 * Form-specific copy (INVALID_KEY = "That API key is invalid…") should stay
 * in the caller; this is the fallback for transport-level codes.
 */
export function errorInlineMessage(err: unknown): string {
  switch (codeOf(err)) {
    case 'APP_BRIDGE_TIMEOUT':
      return 'Shopify did not load in time. Reload the page from your Shopify admin.';
    case 'TIMEOUT':
      return 'Buds did not respond in time. Try again in a moment.';
    case 'BUDS_UNREACHABLE':
      return 'Could not reach Buds. Try again in a moment.';
    default:
      return messageOf(err);
  }
}
